import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  useMediaQuery,
  Skeleton,
  Paper,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import AssuredWorkloadOutlinedIcon from "@mui/icons-material/AssuredWorkloadOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import UnfoldLessOutlinedIcon from "@mui/icons-material/UnfoldLessOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { DataGridPro, GridTreeDataGroupingCell } from "@mui/x-data-grid-pro";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { SidebarContext } from "../Sidebar/SidebarProvider";
import { getEscandalloSubgrupos } from "../../api/Escandallo/escandalloApi";
import DrawerDutties from "./DrawerDutties";
import {
  pageHeroShellSx,
  pageContentCardSx,
  pageFieldSx,
  pageKpiChipSx,
  pageMobileCardSx,
  pageSectionTitleSx,
  pageDataGridSx,
} from "../../styles/pageModernStyles";

const Dutties = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;

  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [search, setSearch] = useState("");
  const [isExpanded, setIsExpanded] = useState(true);
  const [expansionKey, setExpansionKey] = useState(0);

  const pageGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(180deg, ${alpha(theme.palette.background.default, 1)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`
      : "linear-gradient(180deg, #f8fafc 0%, #eef6f8 45%, #f5f3ff 100%)";

  const fieldSx = useMemo(
    () => ({
      ...pageFieldSx(theme),
      minWidth: 0,
      width: "100%",
    }),
    [theme],
  );

  const toolbarIconSx = useMemo(
    () => ({
      border: "1px solid",
      borderColor: alpha(accent, 0.22),
      backgroundColor: theme.palette.background.paper,
      color: accent,
      width: { xs: 40, md: 34 },
      height: { xs: 40, md: 34 },
      minWidth: { xs: 40, md: 34 },
      minHeight: { xs: 40, md: 34 },
      p: 0,
      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
      "&:hover": {
        backgroundColor: alpha(accent, 0.1),
        borderColor: accent,
      },
      "& .MuiSvgIcon-root": { fontSize: "1.05rem" },
    }),
    [theme, accent],
  );

  const expandAll = () => {
    setIsExpanded(true);
    setExpansionKey((prev) => prev + 1);
  };

  const collapseAll = () => {
    setIsExpanded(false);
    setExpansionKey((prev) => prev + 1);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getEscandalloSubgrupos();
      const grouped = data.map((row, i) => ({
        id: `row-${i}`,
        ...row,
      }));
      setTreeData(grouped);
    } catch (err) {
      console.error("Error al obtener datos:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stats = useMemo(() => {
    const groups = new Set(treeData.map((r) => r.GRUPO).filter(Boolean));
    return {
      subgroups: treeData.length,
      groups: groups.size,
    };
  }, [treeData]);

  const filteredTreeData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return treeData;
    return treeData.filter((row) => {
      const haystack = [row.GRUPO, row.SUBGRUPO]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [treeData, search]);

  const groupedForMobile = useMemo(() => {
    const map = {};
    filteredTreeData.forEach((row) => {
      const grupo = row.GRUPO || "—";
      if (!map[grupo]) map[grupo] = [];
      map[grupo].push(row);
    });
    return Object.entries(map).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [filteredTreeData]);

  const openRow = (row) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const handleRowClick = (params) => {
    if (!params.row.isGroup) {
      openRow(params.row);
    }
  };

  const handleAddNew = () => {
    setSelectedRow(null);
    setDrawerOpen(true);
  };

  const handleExportToExcel = () => {
    const g = t("DuttiesPage.excelGroup");
    const sg = t("DuttiesPage.excelSubgroup");
    const dc = t("DuttiesPage.dutiesChina");
    const di = t("DuttiesPage.dutiesIndia");
    const cost = t("DuttiesPage.coste");

    const dataToExport = filteredTreeData.map((row) => ({
      [g]: row.GRUPO ?? "",
      [sg]: row.SUBGRUPO ?? "",
      [dc]: row.DutiesChina?.toFixed(2) ?? "",
      [di]: row.DutiesIndia?.toFixed(2) ?? "",
      [cost]: row.Coste?.toFixed(2) ?? "",
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("DuttiesPage.excelSheet"));

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `Dutties_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const columns = useMemo(
    () => [
      {
        field: "DutiesChina",
        headerName: t("DuttiesPage.dutiesChina"),
        width: 130,
        flex: 0.8,
        minWidth: 110,
        align: "right",
        headerAlign: "right",
        renderCell: (params) =>
          params.value != null ? Number(params.value).toFixed(2) : "—",
      },
      {
        field: "DutiesIndia",
        headerName: t("DuttiesPage.dutiesIndia"),
        width: 130,
        flex: 0.8,
        minWidth: 110,
        align: "right",
        headerAlign: "right",
        renderCell: (params) =>
          params.value != null ? Number(params.value).toFixed(2) : "—",
      },
      {
        field: "Coste",
        headerName: t("DuttiesPage.coste"),
        width: 110,
        flex: 0.7,
        minWidth: 90,
        align: "right",
        headerAlign: "right",
        renderCell: (params) =>
          params.value != null ? Number(params.value).toFixed(2) : "—",
      },
    ],
    [t],
  );

  const renderMobileSubgroup = (row) => (
    <Box
      key={row.id}
      onClick={() => openRow(row)}
      sx={{
        ...pageMobileCardSx(theme, { accentColor: accent }),
        cursor: "pointer",
        "&:active": { transform: "scale(0.995)" },
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", mb: 0.75 }}>
        {row.SUBGRUPO}
      </Typography>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        <Chip
          size="small"
          label={`🇨🇳 ${row.DutiesChina != null ? Number(row.DutiesChina).toFixed(2) : "—"}%`}
          sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
        />
        <Chip
          size="small"
          label={`🇮🇳 ${row.DutiesIndia != null ? Number(row.DutiesIndia).toFixed(2) : "—"}%`}
          sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${t("DuttiesPage.coste")}: ${row.Coste != null ? Number(row.Coste).toFixed(2) : "—"}`}
          sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600 }}
        />
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        minHeight: "100dvh",
        boxSizing: "border-box",
        overflow: { xs: "auto", md: "hidden" },
        background: pageGradient,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 2 },
        maxWidth: 1200,
        width: "100%",
        ...contentMargin,
      }}
    >
      {/* Hero */}
      <Box
        sx={{
          ...pageHeroShellSx(theme, { sticky: !isMobile }),
          mb: 1.5,
          flexShrink: 0,
        }}
      >
        <Stack spacing={{ xs: 1.25, md: 1 }} sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            spacing={{ xs: 1.25, md: 1 }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{ minWidth: 0, flex: 1 }}
            >
              <Box
                sx={{
                  width: { xs: 44, md: 40 },
                  height: { xs: 44, md: 40 },
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(accent, 0.14),
                  color: theme.palette.primary.dark,
                }}
              >
                <AssuredWorkloadOutlinedIcon
                  sx={{ fontSize: { xs: 22, md: 20 } }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    fontSize: { xs: "1.05rem", md: "1.12rem" },
                  }}
                >
                  {t("sidebar.duties")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.35,
                    display: { xs: "block", md: "none" },
                    fontSize: "0.8rem",
                    lineHeight: 1.4,
                  }}
                >
                  {t("DuttiesPage.heroSubtitle")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.5, rowGap: 0.35 }}
                >
                  <Chip
                    label={`${stats.subgroups} ${t("DuttiesPage.subgroupsCount", { defaultValue: "subgrupos" })}`}
                    size="small"
                    variant="outlined"
                    sx={pageKpiChipSx}
                  />
                  <Chip
                    label={`${stats.groups} ${t("DuttiesPage.groupsCount", { defaultValue: "grupos" })}`}
                    size="small"
                    sx={{
                      ...pageKpiChipSx,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.dark,
                    }}
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{
                flexShrink: 0,
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <TextField
                size="small"
                placeholder={t("DuttiesPage.searchPlaceholder", {
                  defaultValue: "Buscar grupo o subgrupo…",
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  ...fieldSx,
                  width: { xs: "100%", md: 240 },
                  order: { xs: 3, md: 0 },
                  flexBasis: { xs: "100%", md: "auto" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {!isMobile ? (
                <>
                  <Tooltip title={t("DuttiesPage.expandAll")}>
                    <span>
                      <IconButton
                        onClick={expandAll}
                        aria-label={t("DuttiesPage.expandAll")}
                        sx={toolbarIconSx}
                      >
                        <UnfoldMoreOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("DuttiesPage.collapseAll")}>
                    <span>
                      <IconButton
                        onClick={collapseAll}
                        aria-label={t("DuttiesPage.collapseAll")}
                        sx={toolbarIconSx}
                      >
                        <UnfoldLessOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : null}

              <Tooltip title={t("DuttiesPage.exportExcel")}>
                <span>
                  <IconButton
                    onClick={handleExportToExcel}
                    aria-label={t("DuttiesPage.exportExcel")}
                    sx={toolbarIconSx}
                  >
                    <DownloadOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t("DuttiesPage.addDuty")}>
                <span>
                  <IconButton
                    onClick={handleAddNew}
                    aria-label={t("DuttiesPage.addDuty")}
                    sx={{
                      ...toolbarIconSx,
                      bgcolor: alpha(accent, 0.12),
                      borderColor: alpha(accent, 0.4),
                    }}
                  >
                    <AddOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Grid / listado */}
      <Box
        sx={{
          ...pageContentCardSx(theme, accent),
          p: 0,
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            {isMobile ? (
              <Stack spacing={1.5}>
                {[...Array(3)].map((_, i) => (
                  <Box key={i}>
                    <Skeleton width="40%" height={20} sx={{ mb: 1 }} />
                    <Stack spacing={1}>
                      <Skeleton variant="rounded" height={72} />
                      <Skeleton variant="rounded" height={72} />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Skeleton variant="rounded" height="calc(100dvh - 220px)" />
            )}
          </Box>
        ) : filteredTreeData.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              m: { xs: 1.5, md: 2 },
              py: 4,
              px: 2,
              textAlign: "center",
              borderRadius: 2.5,
              borderStyle: "dashed",
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <AssuredWorkloadOutlinedIcon
              sx={{ fontSize: 36, color: "action.disabled", mb: 1 }}
            />
            <Typography color="text.secondary" fontWeight={600}>
              {search.trim()
                ? t("DuttiesPage.noSearchResults", {
                    defaultValue: "No hay subgrupos con esa búsqueda",
                  })
                : t("DuttiesPage.noData", {
                    defaultValue: "No hay datos de aranceles",
                  })}
            </Typography>
          </Paper>
        ) : isMobile ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 1.5,
            }}
          >
            <Stack spacing={2}>
              {groupedForMobile.map(([grupo, rows]) => (
                <Box key={grupo}>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <FolderOutlinedIcon
                      sx={{ fontSize: 18, color: "warning.main" }}
                    />
                    <Typography sx={{ ...pageSectionTitleSx, mb: 0 }}>
                      {grupo}
                    </Typography>
                    <Chip
                      label={rows.length}
                      size="small"
                      sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                    />
                  </Stack>
                  <Stack spacing={1}>
                    {rows.map((row) => renderMobileSubgroup(row))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              height: "100%",
              width: "100%",
            }}
          >
            <DataGridPro
              key={`grid-${expansionKey}-${isExpanded ? "open" : "closed"}`}
              rows={filteredTreeData}
              columns={columns}
              loading={loading}
              treeData
              getRowId={(row) => row.id}
              getTreeDataPath={(row) => [row.GRUPO, row.SUBGRUPO]}
              defaultGroupingExpansionDepth={isExpanded ? -1 : 0}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              groupingColDef={{
                headerName: t("DuttiesPage.treeColumn"),
                flex: 1,
                minWidth: 220,
                renderCell: (params) => {
                  const isRoot = params.rowNode.depth === 0;
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      {isRoot ? (
                        <FolderOutlinedIcon
                          sx={{
                            fontSize: 18,
                            color: "warning.main",
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                      <Typography
                        component="span"
                        fontWeight={isRoot ? 800 : 500}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: 0,
                          fontSize: isRoot ? "0.85rem" : "0.82rem",
                        }}
                      >
                        <GridTreeDataGroupingCell {...params} />
                      </Typography>
                    </Box>
                  );
                },
              }}
              sx={{
                ...pageDataGridSx(theme),
                height: "100%",
                flex: 1,
                minHeight: 0,
                border: "none",
                borderRadius: 0,
                cursor: "pointer",
                "& .MuiDataGrid-row:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                },
              }}
            />
          </Box>
        )}
      </Box>

      <DrawerDutties
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        data={selectedRow}
        onSave={fetchData}
      />
    </Box>
  );
};

export default Dutties;
